import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Chip } from '@/components/Chip';
import { Icon } from '@/components/Icon';
import { SignalSheet, type SheetSeed } from '@/features/signals/SignalSheet';
import { TopicDrillDown } from '@/features/topics/TopicDrillDown';
import { fetchCorpusStats, fetchFollowedTowns, fetchTopics, fetchTopicTrends } from '@/lib/api';
import { sayCountries, sayTowns } from '@/lib/corpus';
import { chartMax, STAT_META, type StatKey } from '@/lib/topics';
import './TopicTrends.css';

/**
 * Topic Trends — cross-city analytics, Intelligence Pro.
 *
 * A **Topic** is a saved semantic query counting meetings over time. It is a different object
 * from a Signal: a Topic feeds this chart, a Signal feeds a document feed. "Set Topics" here
 * is not "Manage signals" there.
 *
 * Twelve months render immediately for a brand-new topic — the corpus is already processed and
 * the aggregate is a materialized view, so there is no empty or loading state to design for.
 *
 * **Every stat card and every leaderboard row drills down** into the real, dated meetings
 * behind it. That was a specific fix: drill-downs must show per-topic content rather than one
 * hardcoded dataset reused across every topic.
 */
export function TopicTrends() {
  const [topicId, setTopicId] = useState<string | null>(null);
  const [drill, setDrill] = useState<{ stat?: StatKey; city?: string } | null>(null);
  const [sheet, setSheet] = useState<SheetSeed | null>(null);
  const [managing, setManaging] = useState(false);

  const topicsQuery = useQuery({ queryKey: ['topics'], queryFn: fetchTopics });
  const topics = topicsQuery.data ?? [];
  const activeId = topicId ?? topics[0]?.id ?? null;
  const activeTopic = topics.find((t) => t.id === activeId) ?? null;

  const trendsQuery = useQuery({
    queryKey: ['topic-trends', activeId],
    queryFn: () => fetchTopicTrends(activeId!),
    enabled: Boolean(activeId),
  });

  const townsQuery = useQuery({ queryKey: ['towns'], queryFn: fetchFollowedTowns });
  const statsQuery = useQuery({ queryKey: ['corpus'], queryFn: fetchCorpusStats });

  const trends = trendsQuery.data;
  const max = trends ? chartMax(trends.months) : 1;

  return (
    <div className="trends">
      <header className="trends__head">
        <div>
          <h1 className="trends__title">Topic Trends</h1>
          <p className="trends__subtitle">
            How often a topic comes up across every place we cover, and where.
          </p>
        </div>
        <div className="trends__headActions">
          <button
            type="button"
            className="trends__action"
            onClick={() => setSheet({ mode: 'topic' })}
          >
            <Icon name="plus" /> Track a topic
          </button>
          <button
            type="button"
            className="trends__action"
            onClick={() => setManaging((v) => !v)}
            aria-pressed={managing}
          >
            Set Topics
          </button>
        </div>
      </header>

      <div className="trends__chips u-scroller" role="tablist" aria-label="Tracked topics">
        {topics.map((topic) => (
          <Chip
            key={topic.id}
            icon="trends"
            label={topic.name}
            selected={topic.id === activeId}
            onClick={() => setTopicId(topic.id)}
          />
        ))}
      </div>

      {managing && (
        <div className="trends__manage">
          <p className="u-meta">Set Topics</p>
          <p className="trends__manageHint">
            Rename or remove a tracked topic. This list is separate from your Signals — a topic
            counts meetings for this chart, a signal finds documents for your feed.
          </p>
          <ul className="trends__manageList">
            {topics.map((topic) => (
              <li key={topic.id}>
                <span className="trends__manageName">{topic.name}</span>
                <span className="trends__manageTerms">{topic.terms}</span>
                <button
                  type="button"
                  className="trends__manageBtn"
                  onClick={() =>
                    setSheet({ mode: 'topic', id: topic.id, name: topic.name, terms: topic.terms })
                  }
                >
                  Rename
                </button>
                <button type="button" className="trends__manageBtn">
                  Remove
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!activeTopic && !topicsQuery.isLoading && (
        <p className="trends__empty">No topics tracked yet. “Track a topic” starts one.</p>
      )}

      {activeTopic && trends && (
        <div className="trends__body">
          {/* Stat cards. Every one is a button — every one drills down. */}
          <div className="trends__stats">
            {(Object.keys(STAT_META) as StatKey[]).map((key) => (
              <button
                key={key}
                type="button"
                className="trends__stat"
                onClick={() => setDrill({ stat: key })}
              >
                <span className="trends__statValue">{trends.stats[key]}</span>
                <span className="trends__statLabel">{STAT_META[key].label}</span>
                <span className="trends__statHint">{STAT_META[key].hint}</span>
              </button>
            ))}
          </div>

          <section className="trends__panel">
            <h2 className="u-meta">Meetings per month · {activeTopic.name}</h2>
            <div className="trends__chart" role="img" aria-label={`Twelve months of meetings mentioning ${activeTopic.name}`}>
              {trends.months.map((month) => (
                <div key={month.label} className="trends__bar">
                  <span className="trends__barCount">{month.meetings}</span>
                  <span
                    className="trends__barFill"
                    style={{ height: `${Math.round((month.meetings / max) * 100)}%` }}
                  />
                  {/* "Jun 30"-style month abbreviations are the one accepted exception to
                      mm/dd/yyyy — an axis label is not a date a user reads as a specific day. */}
                  <span className="trends__barLabel">{month.label.split(' ')[0]}</span>
                </div>
              ))}
            </div>
          </section>

          <div className="trends__split">
            <section className="trends__panel">
              <h2 className="u-meta">Top cities</h2>
              <ul className="trends__leaders">
                {trends.topCities.map((city) => (
                  <li key={`${city.city}-${city.region}`}>
                    <button
                      type="button"
                      className="trends__leader"
                      onClick={() => setDrill({ city: city.city })}
                    >
                      <span className="trends__leaderName">
                        {city.city}, {city.region}
                      </span>
                      <span className="trends__leaderBar">
                        <span
                          style={{
                            width: `${Math.round((city.meetings / trends.topCities[0]!.meetings) * 100)}%`,
                          }}
                        />
                      </span>
                      <span className="trends__leaderCount">{city.meetings}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>

            <section className="trends__panel">
              <h2 className="u-meta">By state</h2>
              <ul className="trends__regions">
                {trends.byRegion.map((row) => (
                  <li key={row.region}>
                    <span>{row.region}</span>
                    <span className="trends__regionCount">{row.meetings}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        </div>
      )}

      {drill && activeTopic && (
        <TopicDrillDown
          topicId={activeTopic.id}
          topicName={activeTopic.name}
          filter={drill}
          onClose={() => setDrill(null)}
        />
      )}

      {sheet && (
        <SignalSheet
          seed={sheet}
          followedTownCount={(townsQuery.data ?? []).length}
          corpusSummary={
            statsQuery.data
              ? `${sayTowns(statsQuery.data)} towns · ${sayCountries(statsQuery.data)} countries`
              : 'every place'
          }
          onClose={() => setSheet(null)}
        />
      )}
    </div>
  );
}
